import { useState, useEffect } from 'react';
import { useI18n } from '@expertloop/i18n/react';
import { PdfViewer } from '../../pdf';
import './FileViewerModal.css';

interface FileInfo {
	id: string;
	originalFilename: string;
	mimeType: string;
	size: number;
	downloadUrl?: string;
}

interface FileViewerModalProps {
	file: FileInfo;
	onClose: () => void;
}

function isImageType(mimeType: string): boolean {
	return mimeType.startsWith('image/');
}

function isVideoType(mimeType: string): boolean {
	return mimeType.startsWith('video/');
}

function isAudioType(mimeType: string): boolean {
	return mimeType.startsWith('audio/');
}

function isPdfType(mimeType: string): boolean {
	return mimeType === 'application/pdf';
}

function isTextType(mimeType: string): boolean {
	return mimeType === 'text/plain';
}

export function canPreviewFile(mimeType: string): boolean {
	return isImageType(mimeType)
		|| isVideoType(mimeType)
		|| isAudioType(mimeType)
		|| isPdfType(mimeType)
		|| isTextType(mimeType);
}

export function FileViewerModal({ file, onClose }: FileViewerModalProps) {
	const { t } = useI18n();
	const [textContent, setTextContent] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Fetch text content for text files
	useEffect(() => {
		if (isTextType(file.mimeType) && file.downloadUrl) {
			setIsLoading(true);
			setError(null);
			fetch(file.downloadUrl)
				.then(res => {
					if (!res.ok) throw new Error(t('fileViewer.loadFailed'));
					return res.text();
				})
				.then(text => {
					setTextContent(text);
					setIsLoading(false);
				})
				.catch(() => {
					setError(t('fileViewer.loadFailed'));
					setIsLoading(false);
				});
		}
	}, [file.downloadUrl, file.mimeType, t]);

	// Close on escape key (le lecteur de PDF gère lui-même Échap : il ferme d'abord sa recherche)
	useEffect(() => {
		if (isPdfType(file.mimeType)) return;
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', handleEscape);
		return () => window.removeEventListener('keydown', handleEscape);
	}, [onClose, file.mimeType]);

	const handleDownload = () => {
		if (!file.downloadUrl) return;
		const a = document.createElement('a');
		a.href = file.downloadUrl;
		a.download = file.originalFilename;
		a.target = '_blank';
		a.rel = 'noopener noreferrer';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	// Un PDF se lit dans son propre lecteur (pages multiples, zoom, recherche).
	if (isPdfType(file.mimeType) && file.downloadUrl) {
		return (
			<PdfViewer
				url={file.downloadUrl}
				fileName={file.originalFilename}
				onClose={onClose}
				onDownload={handleDownload}
			/>
		);
	}

	const renderContent = () => {
		if (!file.downloadUrl) {
			return (
				<div className="viewer-error">
					<i className="fas fa-exclamation-triangle"></i>
					<p>{t('fileViewer.urlUnavailable')}</p>
				</div>
			);
		}

		if (isImageType(file.mimeType)) {
			return (
				<div className="viewer-image-container">
					<img
						src={file.downloadUrl}
						alt={file.originalFilename}
						onError={() => setError(t('fileViewer.imageFailed'))}
					/>
				</div>
			);
		}

		if (isVideoType(file.mimeType)) {
			return (
				<div className="viewer-video-container">
					<video
						controls
						autoPlay={false}
						onError={() => setError(t('fileViewer.videoFailed'))}
					>
						<source src={file.downloadUrl} type={file.mimeType} />
						{t('fileViewer.videoUnsupported')}
					</video>
				</div>
			);
		}

		if (isAudioType(file.mimeType)) {
			return (
				<div className="viewer-audio-container">
					<div className="audio-icon">
						<i className="fas fa-music"></i>
					</div>
					<audio
						controls
						autoPlay={false}
						onError={() => setError(t('fileViewer.audioFailed'))}
					>
						<source src={file.downloadUrl} type={file.mimeType} />
						{t('fileViewer.audioUnsupported')}
					</audio>
				</div>
			);
		}

		if (isTextType(file.mimeType)) {
			if (isLoading) {
				return (
					<div className="viewer-loading">
						<i className="fas fa-spinner fa-spin"></i>
						<p>{t('fileViewer.loading')}</p>
					</div>
				);
			}
			return (
				<div className="viewer-text-container">
					<pre>{textContent}</pre>
				</div>
			);
		}

		return (
			<div className="viewer-unsupported">
				<i className="fas fa-file"></i>
				<p>{t('fileViewer.previewUnavailable')}</p>
				<button className="btn btn-primary" onClick={handleDownload}>
					<i className="fas fa-download"></i> {t('fileViewer.downloadToView')}
				</button>
			</div>
		);
	};

	return (
		<div className="file-viewer-overlay" onClick={onClose}>
			<div className="file-viewer-modal" onClick={e => e.stopPropagation()}>
				<div className="file-viewer-header">
					<div className="file-viewer-title">
						<i className="fas fa-file"></i>
						<span>{file.originalFilename}</span>
					</div>
					<div className="file-viewer-actions">
						<button
							className="btn btn-icon"
							onClick={handleDownload}
							title={t('fileViewer.downloadFile')}
						>
							<i className="fas fa-download"></i>
						</button>
						<button
							className="btn btn-icon btn-close"
							onClick={onClose}
							title={t('common.close')}
						>
							<i className="fas fa-times"></i>
						</button>
					</div>
				</div>
				<div className="file-viewer-content">
					{error ? (
						<div className="viewer-error">
							<i className="fas fa-exclamation-triangle"></i>
							<p>{error}</p>
							<button className="btn btn-primary" onClick={handleDownload}>
								<i className="fas fa-download"></i> {t('fileViewer.downloadInstead')}
							</button>
						</div>
					) : (
						renderContent()
					)}
				</div>
			</div>
		</div>
	);
}
